import { defineResource } from '@tnn/core'
import { z } from 'zod'

/**
 * A delivery run (รอบส่ง): a branch hands a driver bills to deliver to the door. Each bill is scanned onto it (`out`,
 * the bill goes out for delivery), then the driver delivers it — the receiver signs and/or a photo, the freight taken at
 * the door on a receipt of the branch — or brings it back with a reason, ready for another run. Submitting numbers it
 * `D6910-0001`; `open` while bills are out on it, `done` once every bill on it is delivered or back.
 */
export const deliveryRunStatuses = ['draft', 'open', 'done', 'cancelled'] as const
export type DeliveryRunStatus = (typeof deliveryRunStatuses)[number]

/** Where an odometer reading is taken: leaving the branch, and back at it */
export const odometerPoints = ['start', 'end'] as const
export type OdometerPoint = (typeof odometerPoints)[number]

/** A run's distance from its odometer readings: null until both are in (or back reads less than leaving) */
export const runDistance = (startKm: number | null | undefined, endKm: number | null | undefined) =>
  startKm == null || endKm == null || endKm < startKm ? null : endKm - startKm

export const deliveryRun = defineResource({
  app: 'tms',
  name: 'deliveryRun',
  path: 'runs',
  schema: z.object({
    date: z.iso.date(),
    /** The branch delivering: the bills' destination */
    branchId: z.string().min(1),
    driverId: z.string().min(1),
    vehicleId: z.string().nullish(),
    /** Odometer readings leaving and back at the branch (the driver's, with a photo each; the branch may correct them) */
    startKm: z.number().int().min(0).max(9_999_999).nullish(),
    endKm: z.number().int().min(0).max(9_999_999).nullish(),
    /** A contract driver's pay for the run; the server fills in their usual pay (anyone else's: none) */
    contractorPay: z.number().min(0).max(1_000_000).nullish(),
    notes: z.string().max(1000).nullish(),
  }),
  fields: {
    number: { readOnly: true, search: true, list: { width: 124, priority: 1 } },
    date: { filter: true, list: { width: 120, priority: 2 }, form: { span: 4 } },
    branchId: {
      type: 'ref',
      ref: { resource: 'company.branch', where: { status: 'active' } },
      filter: true,
      list: { priority: 2 },
      form: { span: 8 },
    },
    driverId: {
      type: 'ref',
      ref: { resource: 'tms.driver', where: { status: 'active', kind: 'driver' } },
      filter: true,
      search: true,
      list: { priority: 1 },
      form: { span: 6, custom: true },
    },
    vehicleId: {
      type: 'ref',
      ref: { resource: 'tms.vehicle', where: { status: 'active' } },
      filter: true,
      list: { width: 120, priority: 2 },
      // Picked from plates only (/api/tms/crew): the counter does not read vehicle records
      form: { span: 6, custom: true },
    },
    status: {
      readOnly: true,
      type: 'select',
      options: deliveryRunStatuses,
      filter: true,
      tones: { draft: 'neutral', open: 'progress', done: 'success', cancelled: 'danger' },
      list: { width: 130, priority: 1 },
    },
    /** Bills put on it (not taken back off), and of those delivered and brought back (set by scans) */
    bills: { readOnly: true, type: 'integer', list: { width: 80, align: 'end', priority: 1 } },
    delivered: { readOnly: true, type: 'integer', list: { width: 90, align: 'end', priority: 2 } },
    failed: { readOnly: true, type: 'integer', list: { width: 90, align: 'end', priority: 2 } },
    startKm: { type: 'integer', list: { align: 'end', priority: 3, hidden: true }, form: { span: 6, section: 'odometer' } },
    endKm: { type: 'integer', list: { align: 'end', priority: 3, hidden: true }, form: { span: 6, section: 'odometer' } },
    /** `endKm` less `startKm`, by the server: what fuel is paid for (payroll.fuelRate) */
    distanceKm: { readOnly: true, type: 'number', list: { align: 'end', width: 90, priority: 2 } },
    /** The photos the driver took of the odometer */
    startKmFileId: { readOnly: true, type: 'file', list: false, import: false, export: false },
    endKmFileId: { readOnly: true, type: 'file', list: false, import: false, export: false },
    /**
     * A contract driver's pay for the run (รถร่วม, their usual trip pay to start with), billed to their partner with its
     * fuel once the run is done and both readings are in; the branch may change it until then. Staff are paid by payroll
     */
    contractorPay: { type: 'money', list: { align: 'end', priority: 3, hidden: true }, form: { span: 6, custom: true } },
    /** The bill for it, made once the run is done with its distance */
    contractorBillId: { readOnly: true, type: 'ref', ref: { resource: 'accounting.bill' }, list: false },
    /** The driver's sign-in account, from `driverId`: who `@own` permissions mean */
    driverUserId: { readOnly: true, list: false, export: false },
    notes: { type: 'textarea', list: false, form: { span: 12 } },
  },
  title: 'number',
  columns: ['number', 'driverId', 'status', 'bills', 'delivered', 'failed', 'date', 'vehicleId', 'branchId'],
  defaultSort: '-date',
  // `scan`: put bills on and take them back off (@branch: the branch's own runs); `deliver`: delivered or not, with
  // proof (@own: its driver; @branch: recorded at the branch when the driver has no phone); `collect`: take the freight
  // at the door on the branch's receipt, the cash in one's own hands (@own: the run's driver only)
  actions: ['read', 'create', 'update', 'delete', 'export', 'print', 'scan', 'deliver', 'collect'],
  ownerField: 'driverUserId',
  branchFields: ['branchId'],
  sections: ['main', 'odometer'],
  submittable: { allowOnSubmit: ['driverId', 'vehicleId', 'startKm', 'endKm', 'contractorPay', 'notes'] },
  card: { title: 'driverId', subtitle: 'number', meta: ['date', 'bills', 'delivered'], status: 'status' },
})
